/**
 * JWT Utilities for Offline Boot
 *
 * Provides standalone JWT decoding and validation without heavy external dependencies.
 * Designed to work within the shared library context.
 */

export interface JWTPayload {
  iss: string;
  sub?: string;
  user_id?: string;
  id?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
  iat?: number;
  exp?: number;
  [key: string]: any;
}

export interface DecodedJWT {
  header: {
    alg: string;
    kid?: string;
    typ?: string;
    [key: string]: any;
  };
  payload: JWTPayload;
  signature: string;
}

/**
 * Decodes a JWT token without validation.
 * Supports basic base64url decoding.
 *
 * @param token The JWT string (header.payload.signature)
 * @returns The decoded parts
 * @throws Error if format is invalid or decoding fails
 */
export function decodeJWT(token: string): DecodedJWT {
  const parts = token.split('.');

  if (parts.length !== 3) {
    throw new Error('Invalid JWT format - expected 3 parts (header.payload.signature)');
  }

  const [headerB64, payloadB64, signature] = parts;

  try {
    const header = JSON.parse(base64UrlDecode(headerB64));
    const payload = JSON.parse(base64UrlDecode(payloadB64));

    return { header, payload, signature };
  } catch (error) {
    throw new Error(`Failed to decode JWT: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * Helper to decode base64url strings.
 * Uses Buffer if available (Node/Electron), otherwise atob (Browser).
 */
function base64UrlDecode(str: string): string {
  // Add padding if needed
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(base64, 'base64').toString('utf-8');
  } else if (typeof atob !== 'undefined') {
    return decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } else {
    throw new Error('No Base64 decoder available (Buffer/atob missing)');
  }
}

/**
 * Extracts userId from JWT payload.
 * Checks 'sub', 'user_id', then 'id'.
 */
export function extractUserId(payload: JWTPayload): string | null {
  if (payload.sub) return payload.sub;
  if (payload.user_id) return payload.user_id;
  if (payload.id) return payload.id;
  return null;
}

/**
 * Checks if a token is expired or will expire within the buffer period.
 *
 * @param payload The decoded JWT payload
 * @param bufferMs Safety buffer in milliseconds (default: 5 minutes)
 * @returns true if expired or expiring soon, false otherwise. Returns false if no 'exp' claim.
 */
export function isTokenExpired(payload: JWTPayload, bufferMs: number = 5 * 60 * 1000): boolean {
  if (!payload.exp) {
    return false;
  }

  const now = Date.now();
  const expiresAt = payload.exp * 1000;

  return now > expiresAt - bufferMs;
}

/**
 * Extracts metadata fields for UserAuth from the JWT payload.
 */
export function extractTokenMetadata(payload: JWTPayload): {
  issuedAt: number | undefined;
  expiresAt: number | undefined;
} {
  return {
    issuedAt: payload.iat ? payload.iat * 1000 : undefined,
    expiresAt: payload.exp ? payload.exp * 1000 : undefined
  };
}
